import React, { useContext, useEffect, useCallback, useState } from 'react';
import {
  Grid,
  Select,
  Dialog,
  Button,
  Snackbar,
  MenuItem,
  TextField,
  Typography,
  InputLabel,
  DialogTitle,
  FormControl,
  DialogContent,
  LinearProgress,
} from '@material-ui/core';
import { DropzoneArea } from 'material-ui-dropzone';
import { yupResolver } from '@hookform/resolvers';
import { useForm, Controller } from 'react-hook-form';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { I18nContext } from 'translations';
import { useHistory } from 'react-router-dom';
import { modalMessageHtml } from 'utils';
import { useStateValue } from 'context/store';
import { listTypePqr, createPqr, listSubjectPqr } from 'context/claims/actions';
import { message as closeMessage } from 'context/commons/actions';
import Loading from 'components/Loading';
import UploadNormalFile from 'components/CustomInputs/UploadNormalFile';
import AWS from 'aws-sdk';
import { uploadFile, configureS3 } from 'utils/aws';
import schema from './schema';
import styles from '../../Tables/styleTableFilters';

const useDropzoneStyles = makeStyles({
  root: {
    minHeight: 80,
    width: '70%',
    padding: '4px 16px',
  },
  chipLabel: {
    fontSize: '0.67rem',
  },
  chip: {
    margin: '2px',
    backgroundColor: '#f2eded',
    border: 'none',
  },
  '@global': {
    '.MuiDropzonePreviewList-imageContainer': {
      padding: '2px !important',
    }
  }
});

const SUBJECT_TYPE_FILTERS = {
  1: { allowedCds: [1, 3, 4, 5, 6] }, // Quejas
  2: { allowedCds: [0, 2] }            // Reclamaciones
};

const PACKAGE_ALLOWED_TYPE_CDS = [8, 9];
const ATTACHMENTS_TOTAL_SIZE_LIMIT = 100 * 1024 * 1024; // 100MB
const MAX_SIZE_PER_ATTACHMENT = 50 * 1024 * 1024;        // 50MB por archivo

const uploadAttachment = async (file, path) => {
  configureS3();
  if (file.size > MAX_SIZE_PER_ATTACHMENT) {
    throw new Error('size_limit');
  }
  const s3 = new AWS.S3(); // eslint-disable-line
  const upload = new AWS.S3.ManagedUpload({
    partSize: 51 * 1024 * 1024, // mayor que MAX_SIZE_PER_ATTACHMENT → siempre un único PUT, sin multipart
    queueSize: 1,
    params: {
      ACL: 'public-read',
      ContentType: file.type || 'application/octet-stream',
      Bucket: 'static.pibox.app',
      Key: `${path}/${file.name}`,
      Body: file,
    },
  });
  return await upload.promise();
};

function ModalClaims ({
  open,
  openClaim,
  classes,
  bookingId,
  packageId,
  hideTitle
}) {
  const { translate } = useContext(I18nContext);
  const dropzoneClasses = useDropzoneStyles();
  const [loading, setLoading] = useState(false);
  const [messageImage, setMessageImage] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const history = useHistory();
  const [{ commons: { message }, claims: { typePqr, loadingTypePqr, subjectPqr, loadingSubjectPqr } }, dispatch] = useStateValue();
  const { handleSubmit, register, control, errors, watch, setValue } = useForm({
    resolver: yupResolver(schema)
  });

  const selectedSubject = watch('subject_cd') ?? subjectPqr?.[0]?.cd;

  const getTypePQR = useCallback(
    async () => {
      await listTypePqr(dispatch);
    }, [dispatch]
  );

  const getSubjectPQR = useCallback(
    async () => {
      await listSubjectPqr(dispatch);
    }, [dispatch]
  );

  useEffect(() => {
    getTypePQR()
    getSubjectPQR()
  },[getTypePQR, getSubjectPQR])

  const filteredTypePqr = React.useMemo(() => {
    if (!typePqr) return [];

    if (packageId) {
      return typePqr.filter(type => PACKAGE_ALLOWED_TYPE_CDS.includes(type.cd));
    }

    if (selectedSubject === 0 || selectedSubject === 3 || selectedSubject === 4) {
      return typePqr.filter(type => type.cd === 7);
    }

    const subjectFilter = SUBJECT_TYPE_FILTERS[selectedSubject];
    if (subjectFilter) {
      return typePqr.filter(type => subjectFilter.allowedCds.includes(type.cd));
    }

    return typePqr.filter(type => type.cd !== 7);
  }, [typePqr, selectedSubject, packageId]);

  useEffect(() => {
    if (!typePqr) return;

    if (selectedSubject === 0 || selectedSubject === 3 || selectedSubject === 4) {
      const notApplicableOption = typePqr.find(type => type.cd === 7);
      if (notApplicableOption) {
        setValue('type_of_request_cd', 7);
      }
    } else {
      const subjectFilter = SUBJECT_TYPE_FILTERS[selectedSubject];
      if (subjectFilter) {
        const firstType = typePqr.find(type => subjectFilter.allowedCds.includes(type.cd));
        if (firstType) setValue('type_of_request_cd', firstType.cd);
      }
    }
  }, [selectedSubject, typePqr, setValue]);

  const uploadFileAws = async (file) => {
    const date = Date.parse(new Date());
    if (file?.size <= 5000000) {
      return await uploadFile(file, `pqr/${date}`);
    } else  {
      return true
    }
  };

  const messageOk = (ticketNumber) => {
    setLoading(false);
    const text = ticketNumber
      ? `${translate('claims.form.mssgOk')}<br/><b>${translate('claims.ticketId')}: ${ticketNumber}</b>`
      : translate('claims.form.mssgOk');
    modalMessageHtml('', text, 'success', translate('accept'), false, null, true).then(() => {
      openClaim();
      history.push('/app/claims');
    });
  };

  const submit = async values => {
    setLoading(true);
    const responseComplaint = values.complaint_letter[0] ? await uploadFileAws(values.complaint_letter[0]) : null;
    const responseInvoice = values.invoice_and_bank_certification[0] ? await uploadFileAws(values.invoice_and_bank_certification[0]) : null;

    if (responseComplaint === true || responseInvoice === true) {
      setMessageImage('claims.errorSizeFile');
      setLoading(false);
      return;
    }

    let attachmentUrls = [];
    if (attachmentFiles.length > 0) {
      const totalSize = attachmentFiles.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > ATTACHMENTS_TOTAL_SIZE_LIMIT) {
        setMessageImage('claims.errorTotalSize');
        setLoading(false);
        return;
      }

      const date = Date.parse(new Date());
      for (let i = 0; i < attachmentFiles.length; i++) {
        const file = attachmentFiles[i];
        setUploadProgress({ current: i + 1, total: attachmentFiles.length, name: file.name });
        try {
          const result = await uploadAttachment(file, `pqr/${date}`);
          attachmentUrls.push(result.Location);
        } catch (err) {
          setUploadProgress(null);
          setMessageImage(err.message === 'size_limit' ? 'claims.errorSizeFile' : 'claims.uploadError');
          setLoading(false);
          return;
        }
      }
      setUploadProgress(null);
    }

    setMessageImage(null);
    const data = {
      ...values,
      complaint_letter: responseComplaint?.Location,
      invoice_and_bank_certification: responseInvoice?.Location,
      ...(attachmentUrls.length > 0 && { attachments: attachmentUrls }),
      ...(packageId && { package_id: packageId })
    };
    const callback = await createPqr(dispatch, bookingId, { pqr: { ...data } });
    callback.status ? messageOk(callback.ticketNumber) : setLoading(false);
  };

  return (
    <Dialog
      open={open}
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
    >
      {!hideTitle && (
        <DialogTitle disableTypography>
          <Typography variant="h6">
            <b>{translate('claims.claimsForm')}</b>
          </Typography>
        </DialogTitle>
      )}

      <DialogContent style={{ padding: '10px 40px 40px' }}>
        {(loadingTypePqr || loadingSubjectPqr) && <Loading />}
        {loading && !uploadProgress && <Loading />}
        <form onSubmit={handleSubmit(submit)} noValidate>
          <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
              {subjectPqr &&
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                  className={classes.filterStyle}
                >
                  <InputLabel>{translate('claims.form.typeOfPqr')}</InputLabel>
                  <Controller
                    control={control}
                    name="subject_cd"
                    label={translate('claims.form.typeOfPqr')}
                    variant="outlined"
                    defaultValue={subjectPqr[0]?.cd}
                    as={
                      <Select>
                        {subjectPqr?.map((type) => (
                          <MenuItem value={type.cd} key={`subject-pqr-${type.cd}`}>
                            {type.text}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                  />
                </FormControl>
              }
            </Grid>
            <Grid item xs={12} sm={6}>
              {filteredTypePqr &&
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                  className={classes.filterStyle}
                >
                  <InputLabel>{translate('claims.form.typeOfRequest')}</InputLabel>
                  <Controller
                    control={control}
                    name="type_of_request_cd"
                    label={translate('claims.form.typeOfRequest')}
                    variant="outlined"
                    defaultValue={filteredTypePqr[0]?.cd}
                    as={
                      <Select>
                        {filteredTypePqr?.map((type) => (
                          <MenuItem value={type.cd} key={`type-pqr-${type.cd}`}>
                            {type.text}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                  />
                </FormControl>
              }
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="mssg"
                control={control}
                variant="outlined"
                defaultValue=""
                render={({ onChange, value }) => (
                  <TextField
                    size="small"
                    variant="outlined"
                    label={translate('claims.form.reason')}
                    fullWidth
                    value={value}
                    onChange={onChange}
                    multiline
                    rows={4}
                    className={classes.filterStyle}
                    error={!!errors.mssg}
                    helperText={errors.mssg && translate(errors.mssg.message)}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6}>
              <UploadNormalFile
                text={translate('claims.form.invoiceAndBankCertification')}
                register={register}
                fileName="invoice_and_bank_certification"
                nameFile={watch('invoice_and_bank_certification')}
                accept="image/*,.pdf"
                helpText=""
              />
              <p style={{ margin: 0 }}><small>{translate('claims.formatFile')}</small></p>
            </Grid>
            <Grid item sm={6}>
              <UploadNormalFile
                text={translate('claims.form.uploadComplaintLetter')}
                register={register}
                fileName="complaint_letter"
                nameFile={watch('complaint_letter')}
                accept="image/*,.pdf"
                helpText=""
              />
              <p style={{ margin: 0 }}><small>{translate('claims.formatFile')}</small></p>
            </Grid>
            <Grid item xs={12} style={{ paddingTop: 0 }}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {translate('claims.form.attachments')}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: 'block' }}>
                {translate('claims.form.attachments.hint')}
              </Typography>
              <DropzoneArea
                onChange={setAttachmentFiles}
                maxFileSize={50000000}
                filesLimit={10}
                dropzoneClass={dropzoneClasses.root}
                dropzoneText={translate('app.bulkLoad.dragFileHereOrClick')}
                getFileLimitExceedMessage={() => translate('app.bulkLoad.pleaseUploadOnlyOneFile')}
                getFileAddedMessage={fileName => `${fileName} ${translate('app.bulkLoad.successfullyAdded')}`}
                getFileRemovedMessage={fileName => `${fileName} ${translate('app.bulkLoad.removed')}`}
                getDropRejectMessage={() => translate('claims.errorAttachmentSize')}
                useChipsForPreview
                previewChipProps={{ size: 'small', classes: { root: dropzoneClasses.chip, label: dropzoneClasses.chipLabel } }}
                showAlerts={['error']}
              />
              <p style={{ margin: 0 }}><small>{translate('claims.formatFileAttachments')}</small></p>
              {uploadProgress && (
                <div style={{ marginTop: 8 }}>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                    {translate('claims.uploadingFile')} {uploadProgress.current} / {uploadProgress.total}: <b>{uploadProgress.name}</b>
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(uploadProgress.current / uploadProgress.total) * 100}
                  />
                </div>
              )}
            </Grid>
            <Grid item xs={12} className="text-right">
              <Button
                variant="outlined"
                type="button"
                color="primary"
                disabled={loading}
                style={{ marginRight: 10, borderRadius: '17px' }}
                onClick={openClaim}
              >
                {translate('cancel')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                style={{ borderRadius: '17px' }}
              >
                {translate('claims.form.send')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <Snackbar
        style={{ zIndex: 99999 }}
        open={!!message}
        autoHideDuration={6000}
        onClose={() => closeMessage(dispatch, null)}
        message={translate(message)}
      />
      <Snackbar
        style={{ zIndex: 99999 }}
        open={!!messageImage}
        autoHideDuration={6000}
        onClose={() => setMessageImage(null)}
        message={translate(messageImage)}
      />
    </Dialog>
  )
}

export default withStyles(styles)(ModalClaims);
